/**
 * TrustAccounting component - Trust ledger with running balances
 *
 * Renders case trust and client trust accounts with transaction history
 * and running balance calculations.
 */

import type { Content, ContentTable, TableCell, TableLayout } from 'pdfmake/interfaces';
import type { TrustAccountingData, TrustAccount, TrustTransaction } from '../types';
import { PDF_COLORS } from '../types';
import { firmCalendarDay } from '../../firmTime';

/**
 * Format currency value
 */
function formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
}

/**
 * Format date for display (MM/DD/YY to match original). OWA-1463: the transaction's day on the
 * firm's clock, as the screen and the trust balances count it.
 */
function formatDate(date: Date | string): string {
    const d = firmCalendarDay(date instanceof Date ? date : new Date(date));
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const year = String(d.getFullYear()).slice(-2);
    return `${month}/${day}/${year}`;
}

/**
 * Create a single trust account section
 */
function createTrustAccountSection(account: TrustAccount): Content[] {
    const content: Content[] = [];

    // Build a single table with header, beginning balance, and transactions
    const body: TableCell[][] = [];

    // Account header row (spans first 4 columns, balance in 5th)
    body.push([
        {
            text: account.type === 'case' ? 'Case Trust' : account.name,
            bold: true,
            fontSize: 10,
            colSpan: 4,
        },
        {},
        {},
        {},
        {
            text: formatCurrency(account.endingBalance),
            bold: true,
            fontSize: 10,
            alignment: 'right',
            fillColor: PDF_COLORS.amountDueYellow,
        },
    ]);

    // Beginning balance row
    body.push([
        { text: 'Beginning Balance', fontSize: 9, colSpan: 4 },
        {},
        {},
        {},
        {
            text: formatCurrency(account.startingBalance),
            alignment: 'right',
            fontSize: 9,
            fillColor: PDF_COLORS.amountDueYellow,
        },
    ]);

    // Transaction rows
    for (const tx of account.transactions) {
        const amountColor = tx.isNegative ? PDF_COLORS.negativeRed : PDF_COLORS.positiveGreen;
        const displayAmount = tx.isNegative ? -Math.abs(tx.amount) : Math.abs(tx.amount);

        body.push([
            {
                text: formatDate(tx.date),
                fontSize: 9,
            },
            {
                text: tx.type,
                fontSize: 9,
            },
            {
                text: tx.description || '',
                fontSize: 9,
                italics: true,
            },
            {
                text: formatCurrency(displayAmount),
                alignment: 'right',
                color: amountColor,
                fontSize: 9,
            },
            {
                text: formatCurrency(tx.runningBalance),
                alignment: 'right',
                fontSize: 9,
                fillColor: PDF_COLORS.amountDueYellow,
            },
        ]);
    }

    const tableLayout: TableLayout = {
        hLineWidth: (i: number) => {
            // Line under header row (gold), no other lines
            if (i === 1) return 1;
            return 0;
        },
        vLineWidth: () => 0,
        hLineColor: () => '#c9a227', // Gold line under header
        paddingLeft: () => 4,
        paddingRight: () => 4,
        paddingTop: () => 3,
        paddingBottom: () => 3,
    };

    const table: ContentTable = {
        table: {
            headerRows: 0,
            widths: [55, 95, '*', 70, 70],
            body,
        },
        layout: tableLayout,
        margin: [0, 15, 0, 0],
    };

    content.push(table);

    return content;
}

/**
 * Create the trust accounting section for PDF
 *
 * @param data - Trust accounting data including case and client trusts
 * @returns pdfmake Content array for trust accounting
 */
export function createTrustAccounting(data: TrustAccountingData): Content[] {
    const content: Content[] = [];

    // Section header
    content.push({
        text: 'Trust Accounting',
        style: 'sectionHeader',
        pageBreak: 'before',
        margin: [0, 0, 0, 15],
    });

    // Case Trust (if present)
    if (data.caseTrust) {
        content.push(...createTrustAccountSection(data.caseTrust));
    }

    // Client Trusts
    for (const clientTrust of data.clientTrusts) {
        // Only show if there are transactions or a starting balance
        if (clientTrust.transactions.length > 0 || clientTrust.startingBalance > 0) {
            content.push(...createTrustAccountSection(clientTrust));
        }
    }

    return content;
}

/**
 * Build trust account data from matter/client trust info
 * This is a helper to convert FinancialHelper trust data to TrustAccountingData
 */
export function buildTrustAccountingData(
    matterTrustInfo: {
        startingBalance: number;
        transactionsThisPeriod: Array<{
            txDate: Date | string;
            txType: string;
            notes?: string;
            amount: number;
            isNegative: boolean;
            currentBalance: number;
        }>;
        endingBalance: number;
    } | null,
    clientTrustInfos: Array<{
        clientName: string;
        startingBalance: number;
        transactionsThisPeriod: Array<{
            txDate: Date | string;
            txType: string;
            notes?: string;
            amount: number;
            isNegative: boolean;
            currentBalance: number;
        }>;
        endingBalance: number;
    }>,
    txTypeMap: Map<string, string>
): TrustAccountingData {
    // Build case trust
    let caseTrust: TrustAccount | undefined;

    if (matterTrustInfo) {
        caseTrust = {
            name: 'Case Trust',
            type: 'case',
            startingBalance: matterTrustInfo.startingBalance,
            endingBalance: matterTrustInfo.endingBalance,
            transactions: matterTrustInfo.transactionsThisPeriod.map(tx => ({
                date: tx.txDate,
                type: txTypeMap.get(tx.txType) || tx.txType,
                description: tx.notes,
                amount: tx.amount,
                isNegative: tx.isNegative,
                runningBalance: tx.currentBalance,
            })),
        };
    }

    // Build client trusts
    const clientTrusts: TrustAccount[] = clientTrustInfos.map(info => ({
        name: info.clientName,
        type: 'client' as const,
        startingBalance: info.startingBalance,
        endingBalance: info.endingBalance,
        transactions: info.transactionsThisPeriod.map(tx => ({
            date: tx.txDate,
            type: txTypeMap.get(tx.txType) || tx.txType,
            description: tx.notes,
            amount: tx.amount,
            isNegative: tx.isNegative,
            runningBalance: tx.currentBalance,
        })),
    }));

    return {
        caseTrust,
        clientTrusts,
    };
}

export default createTrustAccounting;
